import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/telemetry`;

/**
 * Fire a telemetry event to the backend. The server stamps user_email
 * from the session cookie — no need to pass it from the client.
 * Silently no-ops on any network or auth failure.
 */
async function postEvent(event_type, page_slug, error_message = null, metadata = null) {
  try {
    await axios.post(ENDPOINT, { event_type, page_slug, error_message, metadata }, {
      withCredentials: true,
    });
  } catch {
    // Telemetry failures are non-fatal — intentionally swallowed
  }
}

/**
 * useTelemetry — tracks page views and JS errors with user_email stamped server-side.
 *
 * Mount once inside an authenticated layout component (e.g. Layout.jsx).
 * Works alongside the Emergent platform script: that script captures events
 * without user_email; this hook captures the same events WITH user_email,
 * making Unique Visitors and Affected Users metrics accurate.
 */
export function useTelemetry() {
  const location = useLocation();
  const prevPathRef = useRef(null);

  // ── Page view tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    const slug = location.pathname;
    if (slug === prevPathRef.current) return; // skip duplicate fires
    prevPathRef.current = slug;
    postEvent('page_view', slug);
  }, [location.pathname]);

  // ── Global error tracking ──────────────────────────────────────────────────
  useEffect(() => {
    const onError = (event) => {
      const msg  = (event.message || 'Unknown JS error').slice(0, 500);
      const slug = window.location.pathname;
      postEvent('js_error', slug, msg, {
        filename: event.filename,
        lineno:   event.lineno,
        colno:    event.colno,
      });
    };

    const onUnhandledRejection = (event) => {
      const reason = event.reason;
      const msg    = (reason?.message || String(reason) || 'Unhandled promise rejection').slice(0, 500);
      const slug   = window.location.pathname;
      postEvent('unhandled_rejection', slug, msg);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);
}
